import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {WHEN_NOT_IN_OPTIMIZE_MODE} from './optimization';

/**
 * IX3 Interactions Panel Access Control
 *
 * Comprehensive access control for IX3 functionality that includes:
 * - Page branch restrictions (IX3 not supported in page branches)
 * - Role-based permissions mirroring IX2 patterns
 * - Proper user type and site mode restrictions
 * - VCC (Vibe Code Components) restrictions (IX3 not available when editing code components)
 *
 * NOTE: This ability intentionally does NOT restrict to primary locale.
 * IX3 interactions are non-structural (they don't modify the DOM tree)
 * and should be editable on secondary locales. The conditions here are
 * inlined from `canDesign` without the `ONLY_ON_PRIMARY_LOCALE` restriction.
 */
export const canAccessInteractionsPanel = defineAbilityConfig([
  {
    // Main branch access with full permissions
    isEditingCodeComponent: false,
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {canvas: true},
    branches: {main: true},
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  },
] as const);

/**
 * IX2 (legacy) Interactions Panel Access Control
 *
 * Mirrors canAccessInteractionsPanel for the legacy IX1/IX2 panels but is
 * gated by the MULTI_USER_BRANCHING flag on branches. When MUB is off,
 * branch access remains permitted (current behavior). When MUB is on,
 * branch access is denied — both the panel UI and the IX2 sync path
 * read this same ability and align automatically.
 */
export const canAccessIX2InteractionsPanel = defineAbilityConfig([
  {
    // Main branch — always accessible (matches IX3 prereqs).
    abilities: {canDesign: true},
    isEditingCodeComponent: false,
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {canvas: true},
    branches: {main: true},
  },
  {
    // Page branch — accessible only while MUB rollout is OFF.
    // When MUB flips on, this clause stops matching and the user falls
    // through with no permitting clause -> ability returns false.
    abilities: {canDesign: true},
    isEditingCodeComponent: false,
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {canvas: true},
    branches: {branch: true},
    featureFlags: {[FEATURE_FLAGS.MULTI_USER_BRANCHING]: false},
  },
] as const);
